<template>
	<div v-if="layoutView === 'stack'" class="quick-action-bar" ref="rootElement">
		<div class="quick-action-bar__container">
			<BasicButton class="quick-action-bar__button" :class="{ primary: isInLikeList }" @click="toggleLikelist">
				<FontAwesomeIcon :icon="faThumbsUp" />
				<span class="quick-action-bar__button__text">{{ formattedLikes }} </span>
			</BasicButton>

			<BasicButton
				class="quick-action-bar__button"
				:class="{ primary: isInDislikeList }"
				@click="toggleDislikelist"
			>
				<FontAwesomeIcon :icon="faThumbsUp" class="fa-rotate-180" />
				<span class="quick-action-bar__button__text">{{ formattedDislikes }}</span>
			</BasicButton>
		</div>

		<div class="quick-action-bar__container">
			<BasicButton
				v-if="customListsCount || titleObject.__typename === 'Show' || titleObject.__typename === 'Season'"
				class="quick-action-bar__button"
				:class="{ primary: isInWatchList || isInCustomList || isTvShowTracked }"
				@click="onAddToList"
			>
				<FontAwesomeIcon :icon="faBookmark" />
				<span class="quick-action-bar__button__text">{{ listsText }}</span>
			</BasicButton>

			<BasicButton
				v-else
				class="quick-action-bar__button"
				:class="{ primary: isInWatchList || isTvShowTracked }"
				@click="toggleWatchlist"
			>
				<FontAwesomeIcon :icon="faBookmark" />
				<span class="quick-action-bar__button__text">{{ watchlistText }}</span>
			</BasicButton>

			<BasicButton
				:disabled="!enableMarkAsSeen"
				class="quick-action-bar__button"
				:class="{ primary: isInSeenList }"
				@click="toggleSeenlist"
			>
				<FontAwesomeIcon :icon="jwCheckIcon" />
				<span class="quick-action-bar__button__text">{{ seenButtonText }}</span>
			</BasicButton>
		</div>

		<LoginButton location="TitleDetailQuickActionBarV1" class="quick-action-bar__button" />
	</div>

	<div v-else-if="layoutView === 'line'" class="quick-action-bar__line">
		<div
			class="quick-action-bar__line__item"
			v-if="customListsCount || titleObject.__typename === 'Show' || titleObject.__typename === 'Season'"
		>
			<BasicButton
				class="quick-action-bar__button circle"
				:class="{ primary: isInWatchList || isInCustomList || isTvShowTracked }"
				@click="onAddToList"
			>
				<FontAwesomeIcon :icon="faBookmark" />
			</BasicButton>
			<span class="quick-action-bar__button__text">{{ listsText }}</span>
		</div>

		<div class="quick-action-bar__line__item" v-else>
			<BasicButton
				class="quick-action-bar__button circle"
				:class="{ primary: isInWatchList || isTvShowTracked }"
				@click="toggleWatchlist"
			>
				<FontAwesomeIcon :icon="faBookmark" />
			</BasicButton>
			<span class="quick-action-bar__button__text">{{ watchlistText }}</span>
		</div>

		<div class="quick-action-bar__line__item">
			<BasicButton
				class="quick-action-bar__button circle"
				:class="{ primary: isInSeenList }"
				:disabled="!enableMarkAsSeen"
				@click="toggleSeenlist"
			>
				<FontAwesomeIcon :icon="jwCheckIcon" />
			</BasicButton>
			<span class="quick-action-bar__button__text">{{ seenButtonText }}</span>
		</div>

		<div class="quick-action-bar__line__item">
			<BasicButton
				class="quick-action-bar__button circle"
				:class="{ primary: isInLikeList }"
				@click="toggleLikelist"
			>
				<FontAwesomeIcon :icon="faThumbsUp" />
			</BasicButton>
			<span class="quick-action-bar__button__text">{{ formattedLikes }}</span>
		</div>

		<div class="quick-action-bar__line__item">
			<BasicButton
				class="quick-action-bar__button circle"
				:class="{ primary: isInDislikeList }"
				@click="toggleDislikelist"
			>
				<FontAwesomeIcon :icon="faThumbsUp" class="fa-rotate-180" />
			</BasicButton>
			<span class="quick-action-bar__button__text">{{ formattedDislikes }}</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleContentType } from '@/interfaces/snowplow/title-context';
import type { ListMutationTitleDetailParam } from '@/helpers/composables/useListActions';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';

import LoginButton from '@/components/LoginButton.vue';
import BasicButton from '@/ui-components/BasicButton.vue';

import { ModalHelper } from '@/helpers/vfm/modal-helper';

import { SraEventAction } from '@/@types/graphql-types';

import { showAlert } from '@/helpers/vfm/alert-helper';
import { numberToCompactString } from '@/helpers/NumberFormat';
import { captureMessageForSentry } from '@/libraries/sentry';
import { preventNonSignedInAction } from '@/helpers/prevent-non-signed-in-action-helper';

import { useListActions } from '@/helpers/composables/useListActions';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';

import { computed, onMounted, ref, watch } from 'vue';
import { useListsCount } from '@/helpers/composables/useListsCount';
import { useTitleDetailInfo } from '../composables/useTitleDetailInfo';
import { faThumbsUp, faBookmark } from '@fortawesome/free-solid-svg-icons';
import { jwCheckIcon } from '@/plugins/global-fontawesome';

const AddToListModal = defineAsyncComponent(() => import('@/components/watchlist/modals/AddToListModal.vue'));

const props = withDefaults(
	defineProps<{
		titleObject: TitleDetail;
		titleContentType?: TitleContentType;
		influencedSponsoredAd?: SponsoredAdFragment;
		additionalContexts?: SnowplowContext[];
		layoutView?: 'stack' | 'line';
	}>(),
	{ additionalContexts: () => [], layoutView: 'stack' }
);

const LIST_TOGGLE_SOURCE = 'quick-action-bar';

const { t } = useI18n();
const { customListsCount, customListsCountRefetch } = useListsCount();

const { isInDislikeList, isInWatchList, isInLikeList, isInSeenList, isInCustomList, isTvShowTracked } =
	useTitleDetailInfo(() => props.titleObject);

const localLikeListAdditions = ref(0);
const localDislikeListAdditions = ref(0);
const localJwVotes = ref(0);
const isInViewport = ref(false);

let observer: IntersectionObserver | null = null;

const interactions = computed(() => {
	if (props.titleObject.__typename === 'Season' && (props.titleObject.show?.totalSeasonCount as number) <= 1) {
		return null;
	}

	return props.titleObject.content.interactions;
});

const rootElement = ref<HTMLElement | null>(null);

onMounted(async () => {
	localLikeListAdditions.value = interactions.value?.likelistAdditions || 0;
	localDislikeListAdditions.value = interactions.value?.dislikelistAdditions || 0;
	localJwVotes.value = interactions.value?.votesNumber || 0;

	observer = new IntersectionObserver(handleIntersection, { root: null, threshold: 0.5 });
	if (rootElement.value != null) observer.observe(rootElement.value);
});

function handleIntersection(entries: IntersectionObserverEntry[]) {
	entries.forEach(entry => {
		if (entry.isIntersecting) {
			isInViewport.value = true;
			localLikeListAdditions.value = interactions.value?.likelistAdditions ?? 0;
			localDislikeListAdditions.value = interactions.value?.dislikelistAdditions ?? 0;
			localJwVotes.value = interactions.value?.votesNumber ?? 0;
		} else {
			isInViewport.value = false;
		}
	});
}

watch(
	() => interactions.value?.likelistAdditions,
	value => {
		localLikeListAdditions.value = value ?? 0;
	},
	{ immediate: true }
);

watch(
	() => interactions.value?.dislikelistAdditions,
	value => {
		localDislikeListAdditions.value = value ?? 0;
	},
	{ immediate: true }
);

watch(
	() => interactions.value?.votesNumber,
	value => {
		localJwVotes.value = value ?? 0;
	},
	{ immediate: true }
);

const formattedLikes = computed(() => formatRating(localLikeListAdditions.value, 'like', localJwVotes.value));

const formattedDislikes = computed(() =>
	formatRating(localDislikeListAdditions.value, 'dislike', interactions.value?.votesNumber)
);

const listsText = computed(() => {
	if (props.titleObject.__typename === 'Movie') {
		return t('WEBAPP_LISTS');
	}

	const isTvShowTracked =
		(props.titleObject.__typename === 'Season' && !!props.titleObject.show?.tvShowTrackingEntry?.createdAt) ||
		(props.titleObject.__typename === 'Show' && !!props.titleObject.tvShowTrackingEntry?.createdAt);

	return isTvShowTracked ? t('WEBAPP_TRACKING') : t('WEBAPP_LISTS');
});

/** Seen is shown only for experiment users and movies. TVST */
const watchlistText = computed(() => {
	if (props.titleObject.__typename === 'Movie') {
		return t('WEBAPP_WATCHLIST');
	} else {
		return isInWatchList.value ? t('WEBAPP_TRACKING') : t('WEBAPP_WATCHLIST_TV_SHOW_START_TRACKING');
	}
});

const seasonNumber = computed(() =>
	props.titleObject.__typename === 'Season' ? props.titleObject.content.seasonNumber : null
);

const seenButtonText = computed(() => {
	if (props.titleObject.__typename === 'Show') {
		return t('WEBAPP_SEENLIST_SEENALL');
	}

	return seasonNumber.value ? `S${seasonNumber.value} ${t('WEBAPP_SEENLIST_SEEN')}` : t('WEBAPP_SEENLIST_SEEN');
});

const objectId = computed(() =>
	props.titleObject.__typename === 'Season' ? props.titleObject.show.objectId : props.titleObject.objectId
);

const objectType = computed(() =>
	props.titleObject.__typename === 'Season' ? props.titleObject.show.objectType : props.titleObject.objectType
);

const titleName = computed(() =>
	props.titleObject.__typename === 'Season' ? props.titleObject.show.content.title : props.titleObject.content.title
);

// Quick actions (except for seen) don't support Seasons
const titleDetailsForMutation = computed<ListMutationTitleDetailParam>(() => {
	const titleDetails: ListMutationTitleDetailParam = {
		title: titleName.value,
		objectType: objectType.value,
		objectId: objectId.value,
		contentType: props.titleContentType,
	};

	if (seasonNumber.value) {
		titleDetails.seasonNumber = seasonNumber.value;
	}

	return titleDetails;
});

const id = computed(() =>
	props.titleObject.__typename === 'Season' ? props.titleObject.show.id : props.titleObject.id
);

const enableMarkAsSeen = computed(
	() => props.titleObject.content.isReleased || props.titleObject.__typename === 'Movie'
);

const likeDislikeListMutationVariables = computed(() => {
	const seasonTitle = props.titleObject.__typename === 'Season' ? props.titleObject.show.content.title : undefined;
	return {
		...titleDetailsForMutation.value,
		...(seasonTitle && { seasonTitle }),
	};
});

function formatRating(rating: number, action: 'like' | 'dislike', votes: number = 0) {
	if (votes < 30) {
		return action === 'like' ? t('WEBAPP_LIKE') : t('WEBAPP_DISLIKE');
	}
	if (rating >= 10000) return numberToCompactString(rating, 0);
	return numberToCompactString(rating, 1);
}

const listActions = useListActions();

async function toggleWatchlist() {
	const trackingPayload = {
		source: LIST_TOGGLE_SOURCE,
	};
	listActions.setInWatchlist(id.value, isInWatchList, titleDetailsForMutation, trackingPayload);
}

const { trackSraEvent } = useBackendTracking();

async function onAddToList() {
	const preventAction = preventNonSignedInAction();

	// track influenced campaign metrics
	const influenced = !!props.influencedSponsoredAd?.bidId;
	if (influenced) {
		trackSraEvent({
			action: SraEventAction.WatchlistClicked,
			sponsoredAd: () => props.influencedSponsoredAd,
			jwEntityID: id.value,
		});
	}

	if (preventAction) {
		return;
	}

	await ModalHelper.openModal(
		AddToListModal,
		{
			titleId: id.value,
			titleSeasonNumber: seasonNumber.value,
			contentType: props.titleContentType,
		},
		{
			cssClass: 'add-to-list-modal',
			id: 'add-to-list-modal',
		}
	);

	customListsCountRefetch();
}

function handleLikeDislike(action: 'like' | 'dislike') {
	if (action === 'like') {
		if (isInLikeList.value) {
			localLikeListAdditions.value--;
		} else if (!isInDislikeList.value) {
			localLikeListAdditions.value++;
		} else {
			localDislikeListAdditions.value--;
			localLikeListAdditions.value++;
		}
	} else if (action === 'dislike') {
		if (isInDislikeList.value) {
			localDislikeListAdditions.value--;
		} else if (!isInLikeList.value) {
			localDislikeListAdditions.value++;
		} else {
			localLikeListAdditions.value--;
			localDislikeListAdditions.value++;
		}
	}
}

function toggleLikelist() {
	const trackingPayload = {
		source: LIST_TOGGLE_SOURCE,
	};

	// Define onComplete callback function
	const onComplete = () => handleLikeDislike('like');

	// track influenced campaign metrics
	const influenced = !!props.influencedSponsoredAd?.bidId;
	if (influenced) {
		trackSraEvent({
			action: SraEventAction.LikelistClicked,
			sponsoredAd: () => props.influencedSponsoredAd,
			jwEntityID: props.titleObject.id,
		});
	}

	listActions.setInLikelist(
		props.titleObject.id,
		isInLikeList,
		isInDislikeList,
		likeDislikeListMutationVariables,
		trackingPayload,
		onComplete
	);
}

function toggleDislikelist() {
	const trackingPayload = {
		source: LIST_TOGGLE_SOURCE,
	};
	const onComplete = () => handleLikeDislike('dislike');

	// track influenced campaign metrics
	const influenced = !!props.influencedSponsoredAd?.bidId;
	if (influenced) {
		trackSraEvent({
			action: SraEventAction.DislikelistClicked,
			sponsoredAd: () => props.influencedSponsoredAd,
			jwEntityID: props.titleObject.id,
		});
	}

	listActions.setInDislikelist(
		props.titleObject.id,
		isInDislikeList,
		isInLikeList,
		likeDislikeListMutationVariables,
		trackingPayload,
		onComplete
	);
}

function toggleSeenlist() {
	const preventAction = preventNonSignedInAction();

	// track influenced campaign metrics
	const influenced = !!props.influencedSponsoredAd?.bidId;
	if (influenced) {
		trackSraEvent({ action: SraEventAction.SeenlistClicked, sponsoredAd: () => props.influencedSponsoredAd });
	}

	if (preventAction) {
		return;
	}

	try {
		const showObjectId = props.titleObject.__typename === 'Season' ? props.titleObject.show.objectId : undefined;

		const setInSeenList = () =>
			listActions.setInSeenlist(
				props.titleObject.id,
				isInSeenList,
				{
					// Not using titleDetailsForMutation here, because we can mark Season as seen
					title: titleName.value,
					objectId: objectId.value,
					contentType: props.titleContentType,
					objectType: objectType.value,
					seasonNumber: seasonNumber.value ?? undefined,
					showObjectId,
				},
				undefined,
				{
					source: LIST_TOGGLE_SOURCE,
				}
			);

		if (props.titleObject.__typename === 'Show') {
			showAlert({
				header: !isInSeenList.value ? t('WEBAPP_SEENALL_TITLE_ADD') : t('WEBAPP_SEENALL_TITLE_REMOVE'),
				message: t('WEBAPP_SEENALL_MESSAGE'),
				buttons: [t('WEBAPP_SEENALL_NO'), t('WEBAPP_SEENALL_YES')],
				additionalContexts: props.additionalContexts,
			}).then(clickResult => {
				clickResult && setInSeenList();
			});
		} else {
			setInSeenList();
		}
	} catch (error) {
		const err: any = error;
		captureMessageForSentry(`Problems on toggling a season or tv show: ${err.message}`, {
			where: 'QuickActionBar',
		});
		throw new Error(err.message);
	}
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/mixins.scss';

.quick-action-bar {
	font-size: 16px;
	&__container {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 16px;
		margin-bottom: 16px;
	}

	&__button {
		background-color: $jw-color-primary-dark--lighter !important;
		min-height: 48px;
		color: #c6c8cd;

		&.primary {
			color: var(--jw-yellow-500);
		}

		&.circle {
			border-radius: 50%;
			min-height: 32px;
			width: 32px;
			padding: 0;
			justify-content: center;
			align-items: center;
		}

		&:hover {
			background-color: #4a5460 !important;
		}

		&__text {
			margin-inline-start: 8px;
			font-size: 16px;
			font-weight: 400;
			line-height: 24px;
			letter-spacing: 0.015em;
		}
	}
}

.quick-action-bar__line {
	display: flex;
	gap: 16px;
	flex-direction: row !important;
	justify-content: space-between;

	&__item {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
		.quick-action-bar__button__text {
			margin-inline-start: 0px;
			font-size: 12px;
			color: #d2d3d5;
		}
	}
}
</style>
